/* Loaded only by the ASICS CRO product section. */
const root = document.querySelector('.nexita-asics-cro');
const drawer = document.querySelector('theme-drawer#cart-drawer');
const productData = root?.querySelector('[data-asics-cart-product]');

if (root && drawer && productData && !root.dataset.asicsCartReady) {
  let product;
  try { product = JSON.parse(productData.textContent); } catch { /* Keep the native cart intact. */ }
  if (product?.id && typeof product.handle === 'string' && product.variants?.length) {
    enhanceCart(root, drawer, product);
  }
}

function enhanceCart(root, drawer, product) {
  root.dataset.asicsCartReady = 'true';
  document.body.classList.add('nexita-asics-cart-cro');
  const controller = new AbortController();
  const { signal } = controller;
  const handles = new Set([product.handle]);
  try {
    const listed = JSON.parse(root.querySelector('[data-asics-products]')?.textContent || '[]');
    if (Array.isArray(listed)) listed.forEach((handle) => {
      if (typeof handle === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) handles.add(handle);
    });
  } catch { /* A missing/malformed allowlist limits editing to the embedded product. */ }
  const products = new Map();
  const pendingProducts = new Map();
  const currentProduct = normalizeProduct(product, product.handle);
  if (!currentProduct) {
    delete root.dataset.asicsCartReady;
    document.body.classList.remove('nexita-asics-cart-cro');
    return;
  }
  products.set(currentProduct.handle, currentProduct);
  let frame = 0;
  let busy = false;
  let notice = '';
  let noticeKind = 'success';
  const originalTexts = new Map();

  function normalizeProduct(data, handle) {
    if (!data?.id || data.handle !== handle || !Array.isArray(data.variants) || !data.variants.length || !Array.isArray(data.options)) return null;
    const optionNames = data.options.map((option) => typeof option === 'string' ? option : option?.name);
    if (!optionNames.length || optionNames.some((name) => typeof name !== 'string')) return null;
    if (data.variants.some((variant) => !variant.id || !Array.isArray(variant.options) || variant.options.length !== optionNames.length)) return null;
    return { ...data, optionNames, byId: new Map(data.variants.map((variant) => [String(variant.id), variant])) };
  }

  function requestProduct(handle) {
    if (signal.aborted || !handles.has(handle) || products.has(handle) || pendingProducts.has(handle)) return;
    // Fetch only a handle rendered into this section's allowlist. Failed loads are not retried
    // by DOM mutations; the standard cart remains fully usable without the optional editor.
    const prefix = window.Shopify?.routes?.root || '/';
    const request = fetch(`${prefix}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', signal })
      .then(async (response) => {
        if (!response.ok) return null;
        return normalizeProduct(await response.json(), handle);
      })
      .then((resolved) => {
        if (signal.aborted || !root.isConnected) return;
        if (resolved) products.set(handle, resolved);
        // Re-query current rows rather than capturing a row that a cart update may replace.
        schedule();
      })
      .catch(() => { /* Preserve the native row if this optional product lookup fails. */ });
    pendingProducts.set(handle, request);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function setText(node, value) {
    if (!node || node.textContent === value) return;
    if (!originalTexts.has(node)) originalTexts.set(node, node.textContent);
    node.textContent = value;
  }

  function showNotice(message, kind = 'success') {
    notice = message;
    noticeKind = kind;
    schedule();
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; decorate(); });
  }

  function rowHandle(row) {
    const link = row.querySelector('a.cart-items__title');
    if (!link) return null;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin) return null;
    const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
    if (!match || !handles.has(match[1])) return null;
    return match[1];
  }

  function rowContext(row) {
    const handle = rowHandle(row);
    if (!handle) return null;
    const rowProduct = products.get(handle);
    if (!rowProduct) { requestProduct(handle); return null; }
    const link = row.querySelector('a.cart-items__title');
    const url = new URL(link.href, location.origin);
    const variant = rowProduct.byId.get(row.dataset.key?.split(':')[0]) || rowProduct.byId.get(url.searchParams.get('variant'));
    return variant ? { product: rowProduct, variant } : null;
  }

  function decorate() {
    if (!root.isConnected) { destroy(); return; }
    // Keep the native total, form target, drawer and accelerated checkout untouched.
    setText(drawer.querySelector('.cart__checkout-button .button-text > span:first-child'), 'Passer au paiement');
    const heading = drawer.querySelector('.nexita-cart-drawer__header > div');
    let liveNotice = drawer.querySelector('[data-asics-cart-notice]');
    if (heading && notice && !liveNotice) {
      liveNotice = element('p', 'asics-cart-notice');
      liveNotice.dataset.asicsCartNotice = '';
      liveNotice.setAttribute('role', 'status');
      liveNotice.setAttribute('aria-live', 'polite');
      heading.append(liveNotice);
    }
    if (liveNotice) {
      if (liveNotice.textContent !== notice) liveNotice.textContent = notice;
      if (liveNotice.dataset.kind !== noticeKind) liveNotice.dataset.kind = noticeKind;
      liveNotice.hidden = !notice;
    }

    drawer.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
      const context = rowContext(row);
      const existing = row.querySelector('[data-asics-edit]');
      if (existing && (!context || existing.dataset.productHandle !== context.product.handle || existing.dataset.lineKey !== row.dataset.key)) {
        row.querySelector('[data-asics-editor]')?.remove();
        existing.remove();
      }
      if (!context || row.querySelector('[data-asics-edit]')) return;
      const details = row.querySelector('.cart-items__variants-wrapper');
      if (!details) return;
      const names = context.product.optionNames;
      const editLabel = names.length === 1 && /taille|size|pointure/i.test(names[0]) ? 'Modifier la pointure' : 'Modifier la couleur / pointure';
      const edit = element('button', 'asics-cart-edit', editLabel);
      edit.type = 'button';
      edit.dataset.asicsEdit = '';
      edit.dataset.productHandle = context.product.handle;
      edit.dataset.lineKey = row.dataset.key;
      edit.setAttribute('aria-expanded', 'false');
      details.append(edit);
    });
    lockNativeControls(busy);
  }

  function lockNativeControls(locked) {
    drawer.querySelectorAll('.cart-items__quantity-controls, .cart__ctas, [data-asics-edit]').forEach((node) => {
      if (locked && !node.inert) {
        node.inert = true;
        node.dataset.asicsInert = '';
      } else if (!locked && node.hasAttribute('data-asics-inert')) {
        node.inert = false;
        delete node.dataset.asicsInert;
      }
    });
  }

  function closeEditor(row) {
    row.querySelector('[data-asics-editor]')?.remove();
    const edit = row.querySelector('[data-asics-edit]');
    edit?.setAttribute('aria-expanded', 'false');
    edit?.focus();
  }

  function openEditor(row, button) {
    if (busy) return;
    if (row.querySelector('[data-asics-editor]')) { closeEditor(row); return; }
    const context = rowContext(row);
    if (!context) return;
    const { product: rowProduct, variant: current } = context;
    const { variants, optionNames } = rowProduct;
    const editor = element('div', 'asics-cart-editor');
    editor.dataset.asicsEditor = '';
    editor.dataset.productHandle = rowProduct.handle;
    editor.dataset.lineKey = row.dataset.key;
    editor.dataset.currentVariant = String(current.id);
    editor.id = `asics-edit-${row.dataset.key.replace(/[^a-z0-9_-]/gi, '-')}`;
    button.setAttribute('aria-controls', editor.id);
    button.setAttribute('aria-expanded', 'true');
    optionNames.forEach((name, index) => {
      const label = element('label', 'asics-cart-editor__field');
      label.append(element('span', '', name));
      const select = element('select');
      select.dataset.asicsOption = String(index);
      const placeholder = new Option(`Choisir ${/taille|size|pointure/i.test(name) ? 'ma pointure' : name.toLowerCase()}`, '');
      select.append(placeholder);
      [...new Set(variants.map((variant) => variant.options[index]))].forEach((value) => {
        select.append(new Option(value, value));
      });
      select.value = current.options[index];
      label.append(select);
      editor.append(label);
    });
    const help = element('p', 'asics-cart-editor__help', 'La quantité et les autres articles de votre panier seront conservés.');
    editor.append(help);
    const actions = element('div', 'asics-cart-editor__actions');
    const save = element('button', 'asics-cart-save', 'Enregistrer');
    save.type = 'button';
    save.dataset.asicsSave = '';
    const cancel = element('button', 'asics-cart-cancel', 'Annuler');
    cancel.type = 'button';
    cancel.dataset.asicsCancel = '';
    actions.append(save, cancel);
    const error = element('p', 'asics-cart-editor__error');
    error.dataset.asicsEditError = '';
    error.setAttribute('role', 'status');
    editor.append(actions, error);
    button.after(editor);
    refreshEditor(editor);
    editor.querySelector('select')?.focus();
  }

  function refreshEditor(editor) {
    const rowProduct = products.get(editor.dataset.productHandle);
    if (!rowProduct) return;
    const { variants } = rowProduct;
    const selects = [...editor.querySelectorAll('[data-asics-option]')];
    // A color choice restricts sizes; unavailable sizes never silently switch to another size.
    selects.forEach((select, index) => {
      [...select.options].forEach((option) => {
        if (!option.value) return;
        option.disabled = !variants.some((variant) => variant.available && variant.options[index] === option.value &&
          selects.slice(0, index).every((earlier, i) => !earlier.value || variant.options[i] === earlier.value));
      });
      if (select.selectedOptions[0]?.disabled) select.value = '';
    });
    const selected = variants.find((variant) => variant.available &&
      selects.every((select, index) => select.value && variant.options[index] === select.value));
    const save = editor.querySelector('[data-asics-save]');
    save.dataset.variantId = selected ? String(selected.id) : '';
    save.disabled = busy || !selected || String(selected.id) === editor.dataset.currentVariant;
  }

  async function readCart() {
    const prefix = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${prefix}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal });
    if (!response.ok) throw new Error('Le panier ne peut pas être vérifié pour le moment.');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Le panier ne peut pas être vérifié pour le moment.');
    return cart;
  }

  function inventoryOf(items) {
    const inventory = new Map();
    items.forEach((item) => {
      const properties = Object.entries(item.properties || {}).map(([key, value]) => [key, String(value ?? '')]);
      properties.sort(([a], [b]) => a.localeCompare(b));
      const signature = JSON.stringify([
        String(item.variant_id || item.id), properties,
        String(item.selling_plan_allocation?.selling_plan?.id || ''),
      ]);
      inventory.set(signature, (inventory.get(signature) || 0) + item.quantity);
    });
    return inventory;
  }

  function sameInventory(actual, expected) {
    return actual.size === expected.size && [...expected].every(([key, quantity]) => actual.get(key) === quantity);
  }

  async function saveEditor(row, editor, save) {
    if (busy || !save.dataset.variantId || save.disabled) return;
    const error = editor.querySelector('[data-asics-edit-error]');
    const updateCart = window.Shopify?.actions?.updateCart;
    if (typeof updateCart !== 'function') {
      error.textContent = 'La modification est momentanément indisponible. Votre article est conservé.';
      return;
    }
    const rowProduct = products.get(editor.dataset.productHandle);
    const selected = rowProduct?.byId.get(save.dataset.variantId);
    if (!selected?.available) return;
    if (editor.dataset.lineKey !== row.dataset.key || rowHandle(row) !== rowProduct.handle) {
      error.textContent = 'Cet article a changé. Fermez puis rouvrez le panier avant de réessayer.';
      return;
    }
    busy = true;
    drawer.setAttribute('data-asics-cart-saving', '');
    lockNativeControls(true);
    error.textContent = '';
    editor.querySelectorAll('button, select').forEach((control) => { control.disabled = true; });
    save.textContent = 'Enregistrement…';
    try {
      // Read the exact current line immediately before updating: quantity, properties and plan
      // are never reconstructed from labels or from a stale product-page selection.
      const cart = await readCart();
      const line = cart.items.find((item) => item.key === row.dataset.key);
      if (!line || String(line.product_id) !== String(rowProduct.id) || !rowProduct.byId.has(String(line.variant_id || line.id))) {
        throw new Error('Cet article a changé. Fermez puis rouvrez le panier avant de réessayer.');
      }
      if (line.parent_relationship || line.item_components?.length) {
        throw new Error('Cet ensemble ne peut pas être modifié ici. Votre panier est conservé.');
      }
      const payload = { id: line.key, merchandiseId: String(selected.id), quantity: line.quantity };
      if (line.properties) payload.attributes = Object.entries(line.properties).map(([key, value]) => ({ key, value: String(value ?? '') }));
      if (line.selling_plan_allocation?.selling_plan?.id) payload.sellingPlanId = String(line.selling_plan_allocation.selling_plan.id);
      // Horizon's installed standard action accepts an existing AJAX line key and merchandiseId.
      // One line update avoids the non-atomic add-new/remove-old pattern and duplicate additions.
      const result = await updateCart({ lines: [payload] }, { event: { context: 'cart' } });
      if (!result?.cart || result.userErrors?.length || result.detail?.didError) {
        throw new Error(result?.userErrors?.[0]?.message || 'La modification n’a pas pu être confirmée. Vérifiez votre panier.');
      }
      // Some cart adapters accept merchandiseId but ignore it on updates. Confirm the exact
      // final inventory, including other lines and custom properties, before claiming success.
      // Combining equal signatures permits Shopify to merge equivalent target-variant lines.
      const expectedItems = cart.items.map((item) => item.key === line.key ? { ...item, variant_id: selected.id } : item);
      const verified = await readCart();
      if (!sameInventory(inventoryOf(verified.items), inventoryOf(expectedItems))) {
        if (sameInventory(inventoryOf(verified.items), inventoryOf(cart.items))) {
          throw new Error('Cette modification n’est pas disponible pour le moment. Votre article et sa quantité sont inchangés.');
        }
        throw new Error('Le panier a changé pendant la modification. Vérifiez la couleur, la pointure et la quantité affichées avant de continuer.');
      }
      showNotice(`Article modifié : ${selected.options.join(' · ')}.`);
      editor.remove();
    } catch (exception) {
      if (exception.name === 'AbortError') return;
      const message = exception.message || 'La modification n’a pas pu être confirmée. Vérifiez votre panier avant de réessayer.';
      if (editor.isConnected) error.textContent = message;
      else showNotice(message, 'error');
      // Never retry a mutation automatically: a lost response can still mean a saved change.
    } finally {
      busy = false;
      drawer.removeAttribute('data-asics-cart-saving');
      lockNativeControls(false);
      if (editor.isConnected) {
        editor.querySelectorAll('button, select').forEach((control) => { control.disabled = false; });
        save.textContent = 'Enregistrer';
        refreshEditor(editor);
      }
      schedule();
    }
  }

  drawer.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    const row = button.closest('.cart-items__table-row[data-key]');
    if (!row) return;
    if (button.hasAttribute('data-asics-edit')) { event.preventDefault(); openEditor(row, button); }
    if (button.hasAttribute('data-asics-cancel') && !busy) { event.preventDefault(); closeEditor(row); }
    if (button.hasAttribute('data-asics-save')) {
      event.preventDefault();
      saveEditor(row, button.closest('[data-asics-editor]'), button);
    }
  }, { signal });

  drawer.addEventListener('change', (event) => {
    if (event.target.matches('[data-asics-option]')) refreshEditor(event.target.closest('[data-asics-editor]'));
  }, { signal });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const fromASICS = event.target instanceof Element && root.contains(event.target);
    const hasASICS = event.lines?.some((line) => [...products.values()].some((item) => item.byId.has(String(line.merchandiseId).split('/').pop())));
    if (event.action !== 'add' || (!fromASICS && !hasASICS)) return;
    // Clear any previous edit notice; the native product form owns add feedback and errors.
    notice = '';
    schedule();
  }, { signal });

  const observer = new MutationObserver(schedule);
  observer.observe(drawer, { childList: true, subtree: true });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof Element && event.target.contains(root)) destroy();
  }, { signal });

  function destroy() {
    observer.disconnect();
    controller.abort();
    cancelAnimationFrame(frame);
    delete root.dataset.asicsCartReady;
    document.body.classList.remove('nexita-asics-cart-cro');
    drawer.removeAttribute('data-asics-cart-saving');
    lockNativeControls(false);
    drawer.querySelectorAll('[data-asics-edit], [data-asics-editor], [data-asics-cart-notice]').forEach((node) => node.remove());
    originalTexts.forEach((value, node) => { if (node.isConnected) node.textContent = value; });
  }

  decorate();
}
